import React, { useEffect, useState } from 'react';
import './ProgramsPage.css';

interface Program {
  id: string;
  name: string;
  institution: string;
  deadline: string;
  application_opens?: string;
  website?: string;
  description?: string;
  eligibility?: string;
  category?: string;
  fields?: string[];
}

const ProgramsPage: React.FC = () => {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [filteredPrograms, setFilteredPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('deadline_asc');
  const [durationFilter, setDurationFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [ignoreRolling, setIgnoreRolling] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const programsPerPage = 20;

  // Load programs data
  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const response = await fetch('/all_programs.json');
        const data = await response.json();
        
        // Transform data to match our program structure
        const programList = Array.isArray(data) ? data : data.programs || [];
        setPrograms(programList);
        setFilteredPrograms(programList);
        setLoading(false);
      } catch (error) {
        console.error('Error loading programs:', error);
        setLoading(false);
      }
    };

    loadPrograms();
  }, []);

  // Get urgency info for a program
  const getUrgencyInfo = (deadline: string) => {
    if (deadline === "Rolling admissions") {
      return { level: "rolling", text: "🔄 Rolling", class: "urgency-rolling" };
    }

    const currentDate = new Date();
    try {
      const deadlineDate = new Date(deadline);
      const daysDiff = Math.ceil((deadlineDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));

      if (daysDiff < 0) {
        return { level: "past", text: "Past", class: "urgency-past" };
      } else if (daysDiff <= 7) {
        return { level: "critical", text: `🚨 ${daysDiff}d left`, class: "urgency-critical" };
      } else if (daysDiff <= 14) {
        return { level: "urgent", text: `⚠️ ${daysDiff}d left`, class: "urgency-urgent" };
      } else if (daysDiff <= 30) {
        return { level: "soon", text: `📅 ${daysDiff}d left`, class: "urgency-soon" };
      } else if (daysDiff <= 90) {
        return { level: "upcoming", text: `📅 ${daysDiff}d left`, class: "urgency-upcoming" };
      } else {
        return { level: "future", text: "🔮 Future", class: "urgency-future" };
      }
    } catch {
      return { level: "unknown", text: "Check Date", class: "urgency-unknown" };
    }
  };

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...programs];

    // First, filter out programs with past deadlines (except rolling admissions)
    const currentDate = new Date();
    filtered = filtered.filter(program => {
      if (program.deadline === "Rolling admissions") return true;
      try {
        const deadlineDate = new Date(program.deadline);
        return deadlineDate >= currentDate;
      } catch {
        // If we can't parse the date, include it anyway
        return true;
      }
    });

    // Apply duration filter
    if (durationFilter) {
      let targetDate: Date;
      
      switch (durationFilter) {
        case '2weeks':
          targetDate = new Date(currentDate.getTime() + 14 * 24 * 60 * 60 * 1000);
          break;
        case '1month':
          targetDate = new Date(currentDate.getTime() + 30 * 24 * 60 * 60 * 1000);
          break;
        case '3months':
          targetDate = new Date(currentDate.getTime() + 90 * 24 * 60 * 60 * 1000);
          break;
        case '6months':
          targetDate = new Date(currentDate.getTime() + 180 * 24 * 60 * 60 * 1000);
          break;
        case '1year':
          targetDate = new Date(currentDate.getTime() + 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          targetDate = new Date(currentDate.getTime() + 365 * 24 * 60 * 60 * 1000);
      }

      filtered = filtered.filter(program => {
        if (program.deadline === "Rolling admissions") return true;
        try {
          const deadlineDate = new Date(program.deadline);
          return currentDate <= deadlineDate && deadlineDate <= targetDate;
        } catch {
          return false;
        }
      });
    }

    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(program => 
        program.category === categoryFilter || 
        (program.fields && program.fields.includes(categoryFilter))
      );
    }

    // Apply rolling filter
    if (ignoreRolling) {
      filtered = filtered.filter(program => program.deadline !== "Rolling admissions");
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'deadline_asc' || sortBy === '') {
        // Chronological sorting (earliest first)
        if (a.deadline === "Rolling admissions" && b.deadline !== "Rolling admissions") return 1;
        if (b.deadline === "Rolling admissions" && a.deadline !== "Rolling admissions") return -1;
        if (a.deadline === "Rolling admissions" && b.deadline === "Rolling admissions") return 0;
        
        try {
          const dateA = new Date(a.deadline);
          const dateB = new Date(b.deadline);
          return dateA.getTime() - dateB.getTime();
        } catch {
          return 0;
        }
      } else if (sortBy === 'deadline_desc') {
        // Latest first
        if (a.deadline === "Rolling admissions" && b.deadline !== "Rolling admissions") return -1;
        if (b.deadline === "Rolling admissions" && a.deadline !== "Rolling admissions") return 1;
        if (a.deadline === "Rolling admissions" && b.deadline === "Rolling admissions") return 0;
        
        try {
          const dateA = new Date(a.deadline);
          const dateB = new Date(b.deadline);
          return dateB.getTime() - dateA.getTime();
        } catch {
          return 0;
        }
      }
      return 0;
    });

    setFilteredPrograms(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [programs, sortBy, durationFilter, categoryFilter, ignoreRolling]);

  // Pagination
  const totalPages = Math.ceil(filteredPrograms.length / programsPerPage);
  const startIndex = (currentPage - 1) * programsPerPage;
  const currentPrograms = filteredPrograms.slice(startIndex, startIndex + programsPerPage);

  if (loading) {
    return (
      <div className="programs-page">
        <div className="container">
          <div className="loading">Loading summer research programs...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="programs-page">
      <div className="container">
        <div className="header">
          <h1>🎓 Summer Research Programs</h1>
          <p className="subtitle">Discover research opportunities with upcoming deadlines</p>
        </div>

        {/* Filters */}
        <div className="filters-card">
          <h5>🔍 Filters & Sorting</h5>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Sort By</label>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className="filter-select"
              >
                <option value="deadline_asc">�� Due Date (Earliest First) ⭐</option>
                <option value="deadline_desc">📅 Due Date (Latest First)</option>
                <option value="name">🔤 Program Name (A-Z)</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Due Within</label>
              <select 
                value={durationFilter} 
                onChange={(e) => setDurationFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">⏰ Any Time</option>
                <option value="2weeks">🚨 2 Weeks</option>
                <option value="1month">⚠️ 1 Month</option>
                <option value="3months">📅 3 Months</option>
                <option value="6months">📅 6 Months</option>
                <option value="1year">📅 1 Year</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Category</label>
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Categories</option>
                <option value="STEM">STEM</option>
                <option value="Medicine & Health">Medicine & Health</option>
                <option value="Engineering">Engineering</option>
                <option value="Computer Science">Computer Science</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={ignoreRolling}
                  onChange={(e) => setIgnoreRolling(e.target.checked)}
                />
                🚫 Hide Rolling Admissions
              </label>
            </div>
          </div>

          <div className="filter-actions">
            <button 
              onClick={() => {
                setSortBy('deadline_asc');
                setDurationFilter('');
                setCategoryFilter('');
                setIgnoreRolling(false);
              }}
              className="clear-filters-btn"
            >
              🔄 Clear All Filters
            </button>
          </div>
        </div>

        {/* Results Header */}
        <div className="results-header">
          <h4>📊 {filteredPrograms.length} Programs Found</h4>
          {(sortBy === 'deadline_asc' || !sortBy) && (
            <small>⭐ Sorted chronologically (most urgent deadlines first)</small>
          )}
        </div>

        {/* Programs Grid */}
        {currentPrograms.length > 0 ? (
          <div className="programs-grid">
            {currentPrograms.map((program, index) => {
              const urgency = getUrgencyInfo(program.deadline);
              
              return (
                <div key={program.id || index} className="program-card">
                  <div className="program-header">
                    <div className="program-title">
                      <h6>{program.name}</h6>
                      <small>{program.institution}</small>
                    </div>
                    <span className={`urgency-badge ${urgency.class}`}>
                      {urgency.text}
                    </span>
                  </div>

                  <div className="program-body">
                    <div className="program-detail">
                      <strong>📅 Deadline:</strong> {program.deadline}
                    </div>
                    {program.application_opens && (
                      <div className="program-detail">
                        <strong>🚀 Opens:</strong> {program.application_opens}
                      </div>
                    )}
                    {program.description && (
                      <p className="program-description">
                        {program.description.length > 200 
                          ? program.description.substring(0, 200) + '...' 
                          : program.description}
                      </p>
                    )}
                    {program.eligibility && (
                      <p className="program-eligibility">
                        <strong>✅ Eligibility:</strong> {' '}
                        {program.eligibility.length > 150 
                          ? program.eligibility.substring(0, 150) + '...' 
                          : program.eligibility}
                      </p>
                    )}
                  </div>

                  <div className="program-footer">
                    {program.website && (
                      <a 
                        href={program.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="website-btn"
                      >
                        🌐 Visit Website
                      </a>
                    )}
                    <div className="urgency-indicator">
                      {program.deadline.includes('January') && program.deadline.includes('2026') && '🚨 URGENT'}
                      {program.deadline.includes('February') && program.deadline.includes('2026') && '⚠️ SOON'}
                      {program.deadline.includes('2027') && '🔮 FUTURE'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-results">
            <h4>🔍 No programs found</h4>
            <p>Try adjusting your filters to see more programs.</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>
            
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            
            <button 
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        )}

        {/* Legend */}
        <div className="legend-card">
          <h6>🏷️ Urgency Legend:</h6>
          <div className="legend-badges">
            <span className="urgency-badge urgency-critical">🚨 Critical (≤7 days)</span>
            <span className="urgency-badge urgency-urgent">⚠️ Urgent (≤14 days)</span>
            <span className="urgency-badge urgency-soon">📅 Soon (≤30 days)</span>
            <span className="urgency-badge urgency-upcoming">📅 Upcoming (≤90 days)</span>
            <span className="urgency-badge urgency-future">🔮 Future (&gt;90 days)</span>
            <span className="urgency-badge urgency-rolling">🔄 Rolling</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramsPage;